import type { TareAppModel } from "../native-modules/types/TareBridge";

export interface Locale {
  language: string;
  country: string | null;
}

export const DEFAULT_LOCALE: Locale = { language: "en", country: null };

/**
 * Deserialize the locale from the TareAppModel
 */
export function deserializeTareLocale(
  locale: TareAppModel["locale"] | null | undefined,
  fallback: Locale = DEFAULT_LOCALE,
): Locale {
  if (!locale) {
    return fallback;
  }

  return {
    language: locale.languageCode,
    country: locale.countryCode || null,
  };
}

// A valid ISO 639 language subtag is 2-3 letters; a region subtag is 2 letters
// (ISO 3166-1 alpha-2). Anything else (empty, numeric UN M.49 like `419`, or
// otherwise malformed) is ignored.
const LANGUAGE_SUBTAG = /^[a-z]{2,3}$/i;
const COUNTRY_SUBTAG = /^[a-z]{2}$/i;

/**
 * Split a locale string (e.g. `en-US`, `fr_CA`, `zh-Hant-TW`, `pt`) into its
 * language and country subtags. Both `-` and `_` are accepted as separators.
 * `language` is lower-cased, `country` upper-cased; each is `undefined` when
 * absent or malformed.
 */
export const parseLocaleString = (locale: string | null | undefined) => {
  const parts = locale?.trim().replace(/_/g, "-").split("-") ?? [];
  const [language] = parts;
  const country = parts.slice(1).find((part) => COUNTRY_SUBTAG.test(part));

  return {
    language: language && LANGUAGE_SUBTAG.test(language) ? language.toLowerCase() : undefined,
    country: country?.toUpperCase(),
  };
};

export const getCountryFromLocaleString = (locale: string | undefined): string | undefined =>
  parseLocaleString(locale).country;

/**
 * Parse a locale string (e.g. `en-US`, `fr_CA`, `pt`) into a `Locale`, or
 * `fallback` (defaults to `DEFAULT_LOCALE`) when the input is missing/empty or
 * has no valid language subtag.
 */
export const deserializeLocaleString = (
  locale: string | null | undefined,
  fallback: Locale = DEFAULT_LOCALE,
): Locale => {
  const { language, country } = parseLocaleString(locale);
  if (!language) {
    return fallback;
  }

  return {
    language,
    country: country ?? null,
  };
};

export const serializeLocaleToBCP47 = ({ language, country }: Locale): string =>
  !language ? DEFAULT_LOCALE.language : country ? `${language}-${country}` : language;
